import React from 'react';
import Box from '@mui/material/Box';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { string } from 'prop-types';

/**
 * Unified loading/processing overlay component for DataGrid
 * Combines DataLoadingCard and DataProcessingCard functionality
 *
 * @param {Object} props
 * @param {string} [props.variant='loading'] - The state variant: 'loading', 'processing', or 'export'
 * @param {string} [props.type] - Deprecated: Use variant instead. For backwards compatibility: 'export' or 'process'
 * @param {string} [props.label] - Optional custom label to display (overrides default messages)
 */
const DataProcessingCard = ({ variant, type, label }) => {
  // Handle backwards compatibility: map old 'type' prop to new 'variant' prop
  let effectiveVariant = variant;
  if (!variant && type) {
    effectiveVariant = type === 'export' ? 'export' : 'processing';
  }
  if (!effectiveVariant) {
    effectiveVariant = 'loading';
  }

  // Determine icon, colors, and messaging based on variant
  const isLoading = effectiveVariant === 'loading';
  const isExport = effectiveVariant === 'export';
  const isProcessing = effectiveVariant === 'processing' || effectiveVariant === 'process';

  const getIcon = () => {
    if (isExport) {
      return (
        <HourglassBottomIcon
          sx={{
            color: '_next.text.secondary',
            fill: '_next.text.secondary',
            height: 24,
            mb: 1,
            width: 24,
          }}
        />
      );
    }
    if (isLoading) {
      return (
        <HourglassBottomIcon
          sx={{
            color: '_next.text.secondary',
            fill: '_next.text.secondary',
            height: 24,
            mb: 1,
            width: 24,
          }}
        />
      );
    }
    // Processing state
    return (
      <CircularProgress
        sx={{
          color: '_next.text.secondary',
          fill: '_next.text.secondary',
          height: 24,
          mb: 1,
          width: 24,
        }}
      />
    );
  };

  const getTitle = () => {
    if (label) return label;
    if (isExport) return 'Exporting data';
    if (isProcessing) return 'Working';
    return 'Loading data';
  };

  const getSubtitle = () => {
    if (isExport) return 'Exporting large data sets may take some time';
    if (isProcessing) return '';
    return 'Large data sets may take some time.';
  };

  return (
    <Box
      alignItems="center"
      bgcolor="#f6f6f6eb" // needed slight opacity
      borderRadius="4px"
      color="_next.text.primary"
      display="flex"
      flexDirection="column"
      fontFamily="Overpass"
      fontWeight={300}
      height="100%"
      justifyContent="center"
      padding={3}
      textAlign="center"
      width="100%"
    >
      {getIcon()}
      <Typography
        sx={{
          color: '_next.text.primary',
          textAlign: 'center',
        }}
        variant="h6"
      >
        {getTitle()}
      </Typography>
      <Typography sx={{ color: '_next.text.primary', textAlign: 'center' }} variant="body1">
        {getSubtitle()}
      </Typography>
      <LinearProgress
        sx={{
          gap: 1,
          height: 4,
          mt: 3,
          width: '15%',
        }}
      />
    </Box>
  );
};

DataProcessingCard.propTypes = {
  label: string,
  type: string,
  variant: string,
};

DataProcessingCard.defaultProps = {
  label: null,
  type: null,
  variant: 'loading',
};

export default DataProcessingCard;
